const AUTH_SESSION_READY = 'ready';
const AUTH_SESSION_IDLE = 'idle';
const AUTH_SESSION_LOADING = 'loading';

let state = {
  session: null,
  status: AUTH_SESSION_IDLE,
  error: null,
};

const listeners = new Set();

const emitChange = () => {
  listeners.forEach((listener) => listener());
};

const normalizeBoolean = (value, fallback = false) =>
  typeof value === 'boolean' ? value : fallback;

const normalizeObject = (value) =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};

const areSessionsEqual = (left, right) =>
  JSON.stringify(left) === JSON.stringify(right);

export const normalizeAuthSession = (rawSession) => {
  if (!rawSession || typeof rawSession !== 'object') {
    return null;
  }

  const user = normalizeObject(rawSession.user);
  const restaurant = normalizeObject(rawSession.restaurant);
  const products = normalizeObject(rawSession.products);
  const productTrials = normalizeObject(rawSession.productTrials);
  const billing = normalizeObject(rawSession.billing);
  const auth = normalizeObject(rawSession.auth);

  return {
    user: {
      id: user.id ?? null,
      username: user.username ?? null,
      name: user.name ?? null,
      isAdmin: normalizeBoolean(user.isAdmin),
      isOwner: normalizeBoolean(user.isOwner),
      cheflistAdmin: normalizeBoolean(user.cheflistAdmin),
      atendabotAtendente: normalizeBoolean(user.atendabotAtendente),
      isFood: typeof user.isFood === 'boolean' ? user.isFood : null,
      integracao: normalizeBoolean(user.integracao),
    },
    restaurant: {
      id: restaurant.id ?? null,
      isActive: typeof restaurant.isActive === 'boolean' ? restaurant.isActive : null,
    },
    products,
    productTrials: {
      atendabotEndsAt: productTrials.atendabotEndsAt ?? null,
    },
    billing: {
      status: billing.status ?? null,
      plan: billing.plan ?? null,
      currentPeriodEnd: billing.currentPeriodEnd ?? null,
      promotionAccessTrialEndsAt: billing.promotionAccessTrialEndsAt ?? null,
      billingExempt: normalizeBoolean(billing.billingExempt),
      ownerId: billing.ownerId ?? billing.owner_id ?? null,
      inherited: normalizeBoolean(billing.inherited),
    },
    auth: {
      contextId: auth.contextId ?? null,
      deviceScope: auth.deviceScope ?? null,
      refreshBound: normalizeBoolean(auth.refreshBound),
      accessTokenExpiresAt: auth.accessTokenExpiresAt ?? null,
    },
  };
};

export const getAuthSessionState = () => state;

export const subscribeAuthSession = (listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const markAuthSessionLoading = () => {
  if (state.session) {
    return;
  }

  if (state.status === AUTH_SESSION_LOADING) {
    return;
  }

  state = {
    ...state,
    status: AUTH_SESSION_LOADING,
    error: null,
  };
  emitChange();
};

export const setAuthSession = (session) => {
  const normalizedSession = normalizeAuthSession(session);

  if (
    state.status === AUTH_SESSION_READY
    && areSessionsEqual(state.session, normalizedSession)
  ) {
    return normalizedSession;
  }

  state = {
    session: normalizedSession,
    status: AUTH_SESSION_READY,
    error: null,
  };
  emitChange();
  return normalizedSession;
};

export const markAuthSessionError = (error) => {
  state = { ...state, status: 'error', error: error?.message || 'Não foi possível verificar sua sessão.' };
  emitChange();
};

export const clearAuthSession = ({ reset = false } = {}) => {
  state = {
    session: null,
    status: reset ? AUTH_SESSION_IDLE : AUTH_SESSION_READY,
    error: null,
  };
  emitChange();
};

export const syncAuthSessionFromPayload = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  if (!Object.prototype.hasOwnProperty.call(payload, 'session')) {
    return null;
  }

  const normalizedSession = normalizeAuthSession(payload.session);
  return setAuthSession(normalizedSession);
};

export const AUTH_SESSION_STATUS = {
  READY: AUTH_SESSION_READY,
  IDLE: AUTH_SESSION_IDLE,
  LOADING: AUTH_SESSION_LOADING,
};
